/**
 * Copyright © 2025-2026 Cognizant Digital Business, Evolutionary AI.
 * All Rights Reserved.
 * Issued under the Academic Public License.
 */
// END COPYRIGHT
import { PROVIDERS } from "./providers";
const STORAGE_KEY = "lmx-byok-api-keys";
// sessionStorage alone lasts as long as the tab stays open, so a TTL is still needed to expire keys.
const TTL_MS = 2 * 24 * 60 * 60 * 1000;
// The keys currently in sessionStorage, minus any that have expired. Rewrites storage whenever that drops one.
const readStore = () => {
    let stored = {};
    try {
        stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "{}") ?? {};
    }
    catch (error) {
        console.debug("Ignoring unreadable BYOK key storage", error);
    }
    // Expired entries drop out here
    const store = {};
    for (const { vendor } of PROVIDERS) {
        const entry = stored[vendor];
        if (entry && entry.expiresAt > Date.now()) {
            store[vendor] = entry;
        }
    }
    if (Object.keys(store).length !== Object.keys(stored).length) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    }
    return store;
};
// The key saved for a provider in sessionStorage, or undefined when none was entered or it has expired.
export const getApiKey = (provider) => readStore()[provider]?.value;
// Save a provider's key to sessionStorage, or forget it when value is empty.
export const setApiKey = (provider, value) => {
    const next = { ...readStore() };
    // JSON.stringify drops the undefined, so clearing a key removes it from storage
    next[provider] = value ? { value, expiresAt: Date.now() + TTL_MS } : undefined;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
};
/**
 * Collect the keys saved in sessionStorage, for sending as `llm_config` on an API request.
 * Keys the user has not entered are left to the Node server's own environment.
 */
export const getByokLlmConfig = () => {
    const store = readStore();
    const llmConfig = {};
    for (const { vendor, field } of PROVIDERS) {
        const stored = store[vendor]?.value;
        if (stored) {
            llmConfig[field] = stored;
        }
    }
    return Object.keys(llmConfig).length > 0 ? llmConfig : undefined;
};
